import { DefinedInstallationType, INSTALLATION_TYPE } from 'app/shared/models/location-details';
import { findFieldById, findSectionById, findSelectFieldById, normalizeSeriesName } from '../base-utils';
import {
    FlexBasicInfoFieldId,
    FlexInfoFieldId,
    FlexSectionIds,
    Mp2BasicInfoFieldId,
    DynamicPhysicalOffsetFieldId,
} from './base-config-models';
import {
    BasicInfoCommonFieldIds,
    ConfigureAlarmsFieldId,
    DataDeliveryFieldId,
    TAssembledApiData,
} from '../common.models';
import {
    FlumeInstallationFieldIds,
    FlumeInstallationMp2FieldIds,
    PipeInstallationFieldIds,
    PipeInstallationMp2FieldIds,
    WeirInstallationFieldIds,
    WeirInstallationMp2FieldIds,
} from '../common.models';
import { AutocompleteField, FieldType, SelectField, TextField } from '../../models/field-models';
import { FlowType, RainGaugeLocation, SampleRateElement } from '../../../models/location-card.model';
import { processNormalizedSectionFields } from '../../core/dynamic-section-parser';
import {
    DynamicSection,
    SensorConfiguration,
    SensorConfigurationCollection,
    SensorConfigurationCollectionRequest,
    MonitoringPointDataSourceRequest,
    SensorConfigurationRequest,
    SensorMonitoringPointDataSource,
    SensorEnablesSettings,
    RegisterEntityTypeToNumber,
} from '../../models/dynamic-section-models';
import { NormalizedSection } from '../../config.models';
import { FlexLocationPayload, FlexConfigurationPayload } from './base-api-models';
import { AlarmDevice, FlexLocationCardState, FlexScanModeOption, FlexScanModeValue } from './models';
import { LocationCommunicationType } from '../../../location-card.constants';
import { DeviceTypeCode, NonSensorDeviceNames } from 'app/shared/enums/device-type-code';
import { INSTALLATION_DEFAULT_CONSTANTS } from './installation-types/config-models';
import { resolveFlexScanModeForIntervals } from './scan-mode';

export function gatherFlexBasicInfoPayload(state: FlexLocationCardState): FlexLocationPayload[] {
    const result: FlexLocationPayload[] = [];

    let mp1Info: Partial<FlexLocationPayload> = {};
    Object.keys(SaveBasicInfoPayload).forEach((key) => {
        mp1Info[key] = SaveBasicInfoPayload[key](state);
    });

    const installationType = SaveInstallationPayload(state, 1);
    mp1Info = { ...mp1Info, ...installationType };

    result.push(mp1Info as FlexLocationPayload);

    const mp2Section = state.formState.config.sections.find((section) => section.id === FlexSectionIds.Mp2BasicInfo);
    if (mp2Section) {
        let mp2Info: Partial<FlexLocationPayload> = {};
        Object.keys(SaveBasicInfoPayloadMp2).forEach((key) => {
            mp2Info[key] = SaveBasicInfoPayloadMp2[key](state);
        });

        const installationType = SaveInstallationPayload(state, 2);
        mp2Info = { ...mp2Info, ...installationType };

        result.push(mp2Info as FlexLocationPayload);
    }

    return result;
}

export const SaveBasicInfoPayload: { [key: string]: (state: FlexLocationCardState) => unknown } = {
    locationName: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.LocationName)?.value,
    assignedRainGaugeId: (state) => {
        const rainGaugeField = findFieldById(state.formState, BasicInfoCommonFieldIds.AssignedRainGauge) as SelectField<
            RainGaugeLocation,
            string
        >;
        const rainGauge = rainGaugeField?.value;

        if (!rainGauge) {
            return null;
        }

        const assignedRainGauge = state.options.rainGauges.data.find((rg) => rg.locationName === rainGauge);

        if (!assignedRainGauge) {
            return null;
        }

        return assignedRainGauge.locationId;
    },
    description: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.Description)?.value,
    installationDate: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.InstallationDate)?.value,
    communicationType: () => LocationCommunicationType.External,
    latitude: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.Latitude)?.value,
    longitude: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.Longitude)?.value,
    manholeaddress: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.ManholeAddress)?.value,
    manholedepth: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.ManholeDepth)?.value,
    qFinalEntityID: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.QFinalEntityID)?.value,
    serialnumber: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.SerialNumber)?.value,
    series: (state) =>
        normalizeSeriesName(findFieldById(state.formState, BasicInfoCommonFieldIds.Series)?.value as string),
    status: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.Status)?.value,
    maintenanceInterval: (state) =>
        findFieldById(state.formState, BasicInfoCommonFieldIds.MaintenanceInterval)?.value ?? null,
    samplerate: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.SampleRate)?.value,
    fastrate: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.FastRate)?.value,
    customProperties: (state) => {
        const customPropertiesSection = findSectionById(
            state.formState,
            BasicInfoCommonFieldIds.CustomPropertiesSection,
        );
        const customProperties: { key: string; value: string }[] = [];

        if (!customPropertiesSection) {
            return [];
        }

        let i = 0;
        while (true) {
            if (customPropertiesSection!.fields[`${BasicInfoCommonFieldIds.CustomPropertyKey}-${i}`] === undefined) {
                break;
            }

            const keyField = customPropertiesSection!.fields[
                `${BasicInfoCommonFieldIds.CustomPropertyKey}-${i}`
            ] as AutocompleteField<string, string>;
            const valueField = customPropertiesSection!.fields[
                `${BasicInfoCommonFieldIds.CustomPropertyValue}-${i}`
            ] as TextField;

            customProperties.push({
                key: keyField.value,
                value: valueField.value,
            });

            i++;
        }

        return customProperties;
    },
};

export const SaveBasicInfoPayloadMp2: { [key: string]: (state: FlexLocationCardState) => unknown } = {
    description: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.Description).value,
    installationDate: (state) => findFieldById(state.formState, Mp2BasicInfoFieldId.InstallationDate).value,
    latitude: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.Latitude).value,
    longitude: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.Longitude).value,
    locationName: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.LocationName).value + '(2)',
    qFinalEntityID: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.QFinalEntityID).value,
    serialnumber: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.SerialNumber).value,
    assignedRainGaugeId: (state) => {
        const rainGaugeField = findFieldById(state.formState, Mp2BasicInfoFieldId.AssignedRainGauge) as SelectField<
            RainGaugeLocation,
            string
        >;
        const rainGauge = rainGaugeField.value;

        if (!rainGauge) {
            return null;
        }

        const assignedRainGauge = state.options.rainGauges.data.find((rg) => rg.locationName === rainGauge);

        if (!assignedRainGauge) {
            return null;
        }

        return assignedRainGauge.locationId;
    },
    series: (state) =>
        normalizeSeriesName(findFieldById(state.formState, BasicInfoCommonFieldIds.Series).value as string),
    status: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.Status).value,
    maintenanceInterval: (state) =>
        findFieldById(state.formState, BasicInfoCommonFieldIds.MaintenanceInterval)?.value ?? null,
    customProperties: (state) => {
        const customPropertiesSection = findSectionById(state.formState, Mp2BasicInfoFieldId.CustomPropertiesSection);
        const customProperties = [];

        let i = 0;
        while (true) {
            if (customPropertiesSection.fields[`${Mp2BasicInfoFieldId.CustomPropertyKey}-${i}`] === undefined) {
                break;
            }

            customProperties.push({
                key: customPropertiesSection.fields[`${Mp2BasicInfoFieldId.CustomPropertyKey}-${i}`].value,
                value: customPropertiesSection.fields[`${Mp2BasicInfoFieldId.CustomPropertyValue}-${i}`].value,
            });

            i++;
        }

        return customProperties;
    },
};

export const SaveInstallationPayload = (state: FlexLocationCardState, mp: 1 | 2) => {
    const basicInfo = state.formState.config.sections.find((s) => s.id === FlexSectionIds.BasicInfo);
    const mp2basicInfo = state.formState.config.sections.find((s) => s.id === FlexSectionIds.Mp2BasicInfo);

    const installationType = (
        mp === 1
            ? basicInfo?.fields[BasicInfoCommonFieldIds.InstallationType]?.value
            : mp2basicInfo?.fields[Mp2BasicInfoFieldId.InstallationType]?.value
    ) as string;

    if (!installationType) {
        console.error('Installation type not found');
        return null;
    }

    const [type, shape] = installationType.split(':').map((s) => s.trim());

    if (type === INSTALLATION_TYPE.PIPE) {
        const FieldIds = mp === 1 ? PipeInstallationFieldIds : PipeInstallationMp2FieldIds;
        return {
            pipe: {
                height: findFieldById(state.formState, FieldIds.Height).value,
                width: findFieldById(state.formState, FieldIds.Width).value,
                lookupDesc: type,
                hc: findFieldById(state.formState, FieldIds.Hc).value,
                roughness: findFieldById(state.formState, FieldIds.Roughness).value,
                effectiveRoughness: findFieldById(state.formState, FieldIds.EffectiveRoughness).value,
                slope: findFieldById(state.formState, FieldIds.Slope).value,
                frictionFactor: findFieldById(state.formState, FieldIds.FrictionFactor).value,
                nominalDiameter: findFieldById(state.formState, FieldIds.NominalDiameter).value,
                startDate: findFieldById(state.formState, FieldIds.StartDate).value,
                installationType: type,
                MonitoringPoint: mp,
                installationID: DefinedInstallationType.Pipe,
                shapeType: shape,
                installationShapeType: shape,
                depthSensor: 100,
                velocitySensor: 100,
                ...INSTALLATION_DEFAULT_CONSTANTS,
            },
            installationID: DefinedInstallationType.Pipe,
            installationtypeid: DefinedInstallationType.Pipe,
            installationShapeType: shape,
        };
    }

    if (type === INSTALLATION_TYPE.WEIR) {
        const FieldIds = mp === 1 ? WeirInstallationFieldIds : WeirInstallationMp2FieldIds;

        return {
            weir: {
                range: findFieldById(state.formState, FieldIds.Range).value,
                capacity: findFieldById(state.formState, FieldIds.Capacity).value,
                height: findFieldById(state.formState, FieldIds.Height).value,
                coefficient: findFieldById(state.formState, FieldIds.Coefficient).value,
                length: findFieldById(state.formState, FieldIds.Length).value,
                breadth: findFieldById(state.formState, FieldIds.Breadth).value,
                lookupDesc: type,
                installationType: type,
                MonitoringPoint: mp,
                installationID: DefinedInstallationType.Weir,
                shapeType: shape,
                installationShapeType: shape,
                ...INSTALLATION_DEFAULT_CONSTANTS,
                depthSensor: 100,
                velocitySensor: 100,
            },
            installationID: DefinedInstallationType.Weir,
            installationtypeid: DefinedInstallationType.Weir,
            installationShapeType: shape,
        };
    }

    if (type === INSTALLATION_TYPE.FLUME) {
        const FieldIds = mp === 1 ? FlumeInstallationFieldIds : FlumeInstallationMp2FieldIds;

        return {
            flume: {
                range: findFieldById(state.formState, FieldIds.Range).value,
                capacity: findFieldById(state.formState, FieldIds.Capacity).value,
                width: findFieldById(state.formState, FieldIds.Width).value,
                lookupDesc: type,
                installationType: type,
                MonitoringPoint: mp,
                installationID: DefinedInstallationType.Flume,
                shapeType: shape,
                installationShapeType: shape,
                depthSensor: 100,
                velocitySensor: 100,
                ...INSTALLATION_DEFAULT_CONSTANTS,
            },
            installationID: DefinedInstallationType.Flume,
            installationtypeid: DefinedInstallationType.Flume,
            installationShapeType: shape,
        };
    }

    console.error('Installation type not supported');
    return null;
};

export function gatherFlexConfigurationInfo(state: FlexLocationCardState): FlexConfigurationPayload | null {
    const flexInfoSection = state.formState.config.sections.find((s) => s.id === FlexSectionIds.FlexInfo);

    if (!flexInfoSection) {
        console.error('FLEX Info section not found');
        return null;
    }

    const telemetryFastInterval = findSelectFieldById<SampleRateElement, number>(
        state.formState,
        DataDeliveryFieldId.PrismDeliveryFast,
    )?.value;
    const telemetryNormalInterval = findSelectFieldById<SampleRateElement, number>(
        state.formState,
        DataDeliveryFieldId.PrismDeliveryNormal,
    )?.value;
    const normalStorageInterval = findSelectFieldById<SampleRateElement, number>(
        state.formState,
        BasicInfoCommonFieldIds.SampleRate,
    )?.value;
    const fastStorageInterval = findSelectFieldById<SampleRateElement, number>(
        state.formState,
        BasicInfoCommonFieldIds.FastRate,
    )?.value;

    if (
        telemetryFastInterval == null ||
        telemetryNormalInterval == null ||
        normalStorageInterval == null ||
        fastStorageInterval == null
    ) {
        console.error('FLEX configuration interval fields not found');
        return null;
    }

    const silt1 = findFieldById(state.formState, FlexInfoFieldId.Silt1, FieldType.Number);
    const silt2 = findFieldById(state.formState, FlexInfoFieldId.Silt2, FieldType.Number);

    return {
        telemetryFastInterval,
        telemetryNormalInterval,
        normalStorageInterval,
        fastStorageInterval,
        scanMode: resolveFlexScanModeForIntervals(
            findSelectFieldById<FlexScanModeOption, FlexScanModeValue>(state.formState, FlexBasicInfoFieldId.ScanMode)
                ?.value,
            normalStorageInterval,
            fastStorageInterval,
        ),
        devices: [gatherConfigureAlarmsFirst(state)],
        silts: [toNumber(silt1?.value), toNumber(silt2?.value)],
        monitorPoint1FlowType: getFlowType(state, FlexInfoFieldId.FlowType1),
        monitorPoint2FlowType: getFlowType(state, FlexInfoFieldId.FlowType2),
    };
}

function toNumber(value: unknown): number {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : 0;
}

function getSelectNumberValue(section: NormalizedSection | undefined, fieldId: string): number | null {
    const field = section?.fields[fieldId];
    if (field?.type !== FieldType.Select || field.value === null || field.value === undefined) {
        return null;
    }

    const numericValue = Number(field.value);
    return Number.isFinite(numericValue) ? numericValue : null;
}

function getFlowType(state: FlexLocationCardState, fieldId: FlexInfoFieldId): FlowType {
    const value = findFieldById(state.formState, fieldId, FieldType.Select)?.value;

    switch (value) {
        case FlowType.Normal:
            return FlowType.Normal;
        case FlowType.WetStagnant:
            return FlowType.WetStagnant;
        case FlowType.UsuallyDry:
            return FlowType.UsuallyDry;
        case FlowType.PumpStation:
            return FlowType.PumpStation;
        default:
            return FlowType.Normal;
    }
}

interface DynamicSensorMetadata {
    id: number | string;
    name: string;
    version: string;
    sensorType: number | string;
}

function getSensorMetadata(section: NormalizedSection): DynamicSensorMetadata | null {
    const id = section.metadata?.id;
    const name = section.metadata?.name;
    const version = section.metadata?.version;
    const sensorType = section.metadata?.sensorType;

    if (
        typeof name !== 'string' ||
        typeof version !== 'string' ||
        (typeof sensorType !== 'number' && typeof sensorType !== 'string')
    ) {
        return null;
    }

    if (typeof id === 'number' || typeof id === 'string') {
        return { id, name, version, sensorType };
    }

    return id === undefined || id === null ? { id: 0, name, version, sensorType } : null;
}

function gatherMonitoringPointDataSource(
    source: SensorMonitoringPointDataSource | null | undefined,
    sensor: NormalizedSection | undefined,
    templates: readonly DynamicSection[] | undefined,
): SensorMonitoringPointDataSource | null {
    if (!source || !sensor) {
        return null;
    }

    const metadata = getSensorMetadata(sensor);
    if (!metadata) {
        return null;
    }

    const parsedSensorId = typeof metadata.id === 'number' ? metadata.id : Number.parseInt(metadata.id, 10);
    const sensorId = Number.isNaN(parsedSensorId) ? 0 : parsedSensorId;
    const template = templates?.find(
        (candidate) =>
            candidate.id.name === metadata.name &&
            candidate.id.version === metadata.version &&
            String(candidate.id.sensorType) === String(metadata.sensorType),
    );
    const result: SensorMonitoringPointDataSource = {
        depthSensorId: sensorId,
        depthEntityType: source.depthEntityType,
    };

    if (template?.sourcesforVelocity?.length && source.velocityEntityType !== undefined) {
        result.velocitySensorId = sensorId;
        result.velocityEntityType = source.velocityEntityType;
    }

    return result;
}

/**
 * Gathers configuration data from all dynamic sections in the form
 *
 * @param state The current location card state
 * @returns Sensor configuration collection with monitoring point preferences
 */
export function gatherFlexDynamicSectionPayload(state: FlexLocationCardState): SensorConfigurationCollection {
    if (!state?.formState?.config?.sections) {
        return {
            mp_1: null,
            mp_2: null,
            sensors: [],
        };
    }

    const result: SensorConfiguration[] = [];

    const dynamicSections = state.formState.config.sections.filter(
        (section): section is NormalizedSection => section.isDynamic === true,
    );

    const flexInfoSection = state.formState.config.sections.find((v) => v.id === FlexSectionIds.FlexInfo);

    const mp1 = getSelectNumberValue(flexInfoSection, FlexInfoFieldId.MP1);
    const mp2 = getSelectNumberValue(flexInfoSection, FlexInfoFieldId.MP2);
    const channel1 = getSelectNumberValue(flexInfoSection, FlexInfoFieldId.Channel1);
    const channel2 = getSelectNumberValue(flexInfoSection, FlexInfoFieldId.Channel2);

    dynamicSections.forEach((section: NormalizedSection) => {
        const sensorIndex = section.metadata?.sensorIndex;
        if (sensorIndex !== 0 && sensorIndex !== 1) {
            return;
        }

        const mp = sensorIndex === 0 ? mp1 : mp2;
        const channel = sensorIndex === 0 ? channel1 : channel2;
        const physicalOffsetFieldId =
            mp === 1 ? DynamicPhysicalOffsetFieldId.PhysicalOffset1 : DynamicPhysicalOffsetFieldId.PhysicalOffset0;
        const physicalOffset = toNumber(findFieldById(state.formState, physicalOffsetFieldId, FieldType.Number)?.value);
        const metadata = getSensorMetadata(section);

        if (!metadata || mp === undefined || mp === null || channel === undefined || channel === null) {
            return;
        }

        const sensorConfig: SensorConfiguration = {
            id: metadata.id,
            sensorName: metadata.name,
            sensorVersion: metadata.version,
            sensorType: metadata.sensorType,
            slaveId: 1,
            channel,
            monitoringPoint: mp,
            physicalOffset,
            settings: {},
        };

        processNormalizedSectionFields(section, sensorConfig.settings);

        result.push(sensorConfig);
    });

    const monitorConfiguration = state.monitorConfiguration;

    const mp1Sensor = dynamicSections.find((s) => s.metadata?.mp === 0);
    const mp2Sensor = dynamicSections.find((s) => s.metadata?.mp === 1);

    const mp1Preferences = gatherMonitoringPointDataSource(
        monitorConfiguration?.mp1Preferences,
        mp1Sensor,
        state.options.loadedDynamicConfigs,
    );
    const mp2Preferences = gatherMonitoringPointDataSource(
        monitorConfiguration?.mp2Preferences,
        mp2Sensor,
        state.options.loadedDynamicConfigs,
    );

    return {
        mp_1: mp1Preferences,
        mp_2: mp2Preferences,
        sensors: result,
    };
}

function gatherConfigureAlarmsFirst(state: FlexLocationCardState): AlarmDevice {
    const pipeHeight = getPipeHeightValue(state, 1);

    const alarmInfo: { [key: string]: (state: FlexLocationCardState) => TAssembledApiData } = {
        fullPipethreshold: () => pipeHeight,
        fullPipeEnable: (state) => findFieldById(state.formState, ConfigureAlarmsFieldId.FullPipeEnable).value,
        highAlaramreshold: (state) => findFieldById(state.formState, ConfigureAlarmsFieldId.HighHighThreshold).value,
        highHighEnable: (state) => findFieldById(state.formState, ConfigureAlarmsFieldId.HighHighEnable).value,
        highHighThreshold: (state) => findFieldById(state.formState, ConfigureAlarmsFieldId.HighHighThreshold).value,
        highLevelEnable: (state) => findFieldById(state.formState, ConfigureAlarmsFieldId.HighLevelEnable).value,
        highLevelThreshold: (state) => findFieldById(state.formState, ConfigureAlarmsFieldId.HighLevelThreshold).value,
        lowLevelEnable: (state) => findFieldById(state.formState, ConfigureAlarmsFieldId.LowLevelEnable).value,
        lowLevelThreshold: (state) => findFieldById(state.formState, ConfigureAlarmsFieldId.LowLevelThreshold).value,
        highFlowEnable: (state) => findFieldById(state.formState, ConfigureAlarmsFieldId.HighFlowEnable).value,
        highFlowThreshold: (state) => findFieldById(state.formState, ConfigureAlarmsFieldId.HighFlowThreshold).value,
        batteryLowEnable: (state) => findFieldById(state.formState, ConfigureAlarmsFieldId.BatteryLowEnable).value,
        batteryLowThreshold: (state) =>
            findFieldById(state.formState, ConfigureAlarmsFieldId.BatteryLowThreshold).value,
        prismdeliveryfast: (state) => findFieldById(state.formState, DataDeliveryFieldId.PrismDeliveryFast).value,
        prismdeliverynormal: (state) => findFieldById(state.formState, DataDeliveryFieldId.PrismDeliveryNormal).value,
        name: () => NonSensorDeviceNames.Alarm1,
        type: () => DeviceTypeCode.FlexAlarm,
        installationNumber: () => 0,
    };

    const alarmResult: Partial<AlarmDevice> = Object.keys(alarmInfo).reduce((acc, key) => {
        acc[key] = alarmInfo[key](state);
        return acc;
    }, {});

    return alarmResult as AlarmDevice;
}

function getPipeHeightValue(state: FlexLocationCardState, mp: 1 | 2): number {
    const basicInfo = state.formState.config.sections.find((s) => s.id === FlexSectionIds.BasicInfo);
    const mp2basicInfo = state.formState.config.sections.find((s) => s.id === FlexSectionIds.Mp2BasicInfo);

    const installationType = (
        mp === 1
            ? basicInfo?.fields[BasicInfoCommonFieldIds.InstallationType].value
            : mp2basicInfo?.fields[Mp2BasicInfoFieldId.InstallationType].value
    ) as string;

    if (!installationType) {
        console.error('Installation type not found');
        return null;
    }

    const [type] = installationType.split(':').map((s) => s.trim());

    if (type === INSTALLATION_TYPE.PIPE) {
        const FieldIds = mp === 1 ? PipeInstallationFieldIds : PipeInstallationMp2FieldIds;
        return findFieldById(state.formState, FieldIds.Height, FieldType.Number).value;
    }

    if (type === INSTALLATION_TYPE.WEIR) {
        const FieldIds = mp === 1 ? WeirInstallationFieldIds : WeirInstallationMp2FieldIds;
        return findFieldById(state.formState, FieldIds.Height, FieldType.Number).value;
    }

    return 0;
}

export function transformToCollectionRequest(
    collection: SensorConfigurationCollection,
): SensorConfigurationCollectionRequest {
    const transformDataSource = (
        source: SensorMonitoringPointDataSource | null | undefined,
    ): MonitoringPointDataSourceRequest | null => {
        if (!source) return null;

        const request: MonitoringPointDataSourceRequest = {
            depthSensorId: source.depthSensorId ?? 0,
            depthEntityType: source.depthEntityType ? RegisterEntityTypeToNumber[source.depthEntityType] : 0,
        };

        if (source.velocityEntityType !== undefined) {
            request.velocitySensorId = source.velocitySensorId ?? 0;
            request.velocityEntityType = RegisterEntityTypeToNumber[source.velocityEntityType];
        }

        return request;
    };

    const transformSensor = (sensor: SensorConfiguration): SensorConfigurationRequest => {
        return {
            id: typeof sensor.id === 'number' ? sensor.id : parseInt(sensor.id ?? '', 10) || 0,
            sensorName: sensor.sensorName,
            sensorVersion: sensor.sensorVersion,
            sensorType:
                typeof sensor.sensorType === 'number' ? sensor.sensorType : parseInt(sensor.sensorType, 10) || 0,
            slaveId: sensor.slaveId,
            channel: sensor.channel,
            monitoringPoint: sensor.monitoringPoint,
            physicalOffset: sensor.physicalOffset,
            physicalOffsetUnit: 0,
            physicalOffsetReference: 1,
            metadata: [
                {
                    name: sensor.sensorName,
                    lastUpdatedUtc: new Date().toISOString(),
                    version: 0,
                },
            ],
            settings: Object.entries(sensor.settings).reduce((acc, [key, value]) => {
                acc[key] = value;
                return acc;
            }, {} as Record<string, string | number | boolean | SensorEnablesSettings>),
        };
    };

    return {
        mp_1: transformDataSource(collection.mp_1),
        mp_2: transformDataSource(collection.mp_2),
        sensors: collection.sensors.map(transformSensor),
    };
}
